import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, X, Search } from 'lucide-react';
import { cn } from '../../lib/utils';

export function YearMultiSelect({
  onYearsChange,
  selectedYears = [],
  className
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Cerrar dropdown al hacer clic fuera o presionar Escape
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleEscapeKey(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen]);

  // Generar años desde 1990 hasta el año actual
  const currentYear = new Date().getFullYear();
  const allYears = [];
  for (let year = currentYear; year >= 1990; year--) {
    allYears.push(year);
  }

  // Filtrar años basado en el término de búsqueda
  const filteredYears = allYears.filter(year =>
    year.toString().includes(searchTerm)
  );

  const handleYearToggle = (year) => {
    const newSelection = selectedYears.includes(year)
      ? selectedYears.filter(y => y !== year)
      : [...selectedYears, year].sort((a, b) => b - a);

    onYearsChange(newSelection);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onYearsChange([]);
  };


  const displayText = () => {
    if (selectedYears.length === 0) {
      return 'Seleccionar años de nacimiento';
    }
    if (selectedYears.length === 1) {
      return `Año ${selectedYears[0]}`;
    }
    if (selectedYears.length <= 3) {
      return selectedYears.sort((a, b) => b - a).join(', ');
    }
    return `${selectedYears.length} años seleccionados`;
  };

  return (
    <div ref={dropdownRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => {
          if (!isOpen) {
            setSearchTerm(''); // Limpiar búsqueda al abrir
          }
          setIsOpen(!isOpen);
        }}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          selectedYears.length > 0 ? "text-foreground" : "text-muted-foreground"
        )}
      >
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4" />
          <span>{displayText()}</span>
        </div>
        <div className="flex items-center space-x-1">
          {selectedYears.length > 0 && (
            <X
              className="h-4 w-4 opacity-50 hover:opacity-100"
              onClick={handleClear}
            />
          )}
          <ChevronDown className="h-4 w-4 opacity-50" />
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-11 left-0 z-50 w-full bg-background border border-border rounded-md shadow-lg max-h-64 overflow-hidden">
          <div className="p-3 border-b border-border">
            {/* Header con botón limpiar */}
            {selectedYears.length > 0 && (
              <div className="flex justify-end mb-3">
                <button
                  type="button"
                  onClick={() => onYearsChange([])}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Limpiar todo
                </button>
              </div>
            )}

            {/* Buscador */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Buscar año..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-8 pl-9 pr-3 border border-input rounded text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="max-h-48 overflow-y-auto">
            <div className="p-1">
              {filteredYears.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                  No se encontraron años
                </div>
              ) : (
                filteredYears.map((year) => (
                <div
                  key={year}
                  onClick={() => handleYearToggle(year)}
                  className={cn(
                    "flex items-center space-x-2 px-3 py-2 text-sm cursor-pointer rounded hover:bg-accent",
                    selectedYears.includes(year) ? "bg-accent text-accent-foreground" : ""
                  )}
                >
                  <div
                    className={cn(
                      "w-4 h-4 border rounded flex items-center justify-center",
                      selectedYears.includes(year)
                        ? "bg-primary border-primary"
                        : "border-muted-foreground"
                    )}
                  >
                    {selectedYears.includes(year) && (
                      <div className="w-2 h-2 bg-primary-foreground rounded-sm" />
                    )}
                  </div>
                  <span>{year}</span>
                </div>
                ))
              )}
            </div>
          </div>

          <div className="p-3 border-t border-border">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full px-3 py-2 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90"
            >
              Aplicar ({selectedYears.length} años)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

